/**
 * Header and footer navigation.
 *
 * The snapshot that seeds the Navigation collection. After the first seed the
 * database decides what the menus say and a super admin reorders them in the
 * panel; this file stays as the committed default and the record of why the
 * structure is what it is.
 *
 * Four top-level items: what we do, what it achieved, who we are, how to help.
 * An earlier draft had seven, which is more than a floating header can hold
 * comfortably.
 *
 * "Where we work" used to be a fifth. It is a section of the homepage now, where
 * a visitor meets the seven places directly beneath the four programmes, and
 * /where-we-work still exists and is linked from that section and the footer.
 */

export type NavLink = { label: string; href: string; description?: string; external?: boolean };

export type NavItem = {
  label: string;
  href: string;
  /** Overrides the "View all …" link at the foot of a dropdown. */
  overviewLabel?: string;
  children?: NavLink[];
};

export const primaryNav: NavItem[] = [
  {
    label: "Our work",
    href: "/our-work",
    overviewLabel: "All programmes and projects",
    children: [
      {
        label: "Women & Girls",
        href: "/our-work/women-girls",
        description: "Menstrual health, wellbeing and livelihood skills",
      },
      {
        label: "Children & Education",
        href: "/our-work/children-education",
        description: "Sarangkot Nestling Home and learning support",
      },
      {
        label: "Health, Relief & Community Resilience",
        href: "/our-work/health-relief-resilience",
        description: "Health access, flood recovery and infrastructure",
      },
      {
        label: "Culture, Environment & Dialogue",
        href: "/our-work/culture-environment-dialogue",
        description: "Waste management, roundtables and public dialogue",
      },
    ],
  },
  {
    label: "Impact & stories",
    href: "/impact",
    overviewLabel: "See the impact record",
    children: [
      {
        label: "Impact record",
        href: "/impact",
        description: "Documented figures, each with its reporting period",
      },
      { label: "News", href: "/news", description: "Announcements and reporting, newest first" },
      {
        label: "Stories from the field",
        href: "/stories",
        description: "Reporting written from documented events",
      },
      { label: "Gallery", href: "/gallery", description: "Photographs and video from programme days" },
      {
        label: "Media & dialogue",
        href: "/media",
        description: "Podcasts, documentaries and roundtables",
      },
    ],
  },
  {
    label: "About",
    href: "/about",
    overviewLabel: "About HISCUS",
    children: [
      { label: "About HISCUS", href: "/about", description: "Story, vision, mission and registration" },
      {
        label: "Transparency",
        href: "/transparency",
        description: "Registration, reports and document status",
      },
      {
        label: "Safeguarding",
        href: "/safeguarding",
        description: "How HISCUS handles consent, images and concerns",
      },
      {
        label: "Contact",
        href: "/contact",
        description: "Enquiries, media requests and local collaboration",
      },
    ],
  },
  {
    label: "Get involved",
    href: "/get-involved",
    overviewLabel: "All the ways to take part",
    children: [
      /*
       * "Support our work" replaces "Partner with us" here and everywhere else.
       *
       * The old label addressed one audience, and the page behind it served
       * seven: foundations, INGOs, embassies, development agencies, companies,
       * individual supporters and the Nepali diaspora. A funder looking for
       * where to give should not have to read "partner" and guess that it means
       * them. /get-involved/partner still resolves; it redirects.
       */
      {
        label: "Support our work",
        href: "/get-involved/support",
        description: "Fund a programme, partner institutionally, or sponsor delivery",
      },
      {
        label: "Volunteer with us",
        href: "/get-involved/volunteer",
        description: "Contribute skills and time to live programmes",
      },
      {
        label: "Charity Trek",
        href: "/get-involved/charity-trek",
        description: "Trek and serve, with Mountain Routes",
      },
      {
        label: "Contact HISCUS",
        href: "/contact",
        description: "Local collaboration, media and research enquiries",
      },
    ],
  },
];

/**
 * The button at the right of the header, and at the foot of the mobile menu.
 *
 * Seeded as the one "utility" navigation row, so its words and destination are
 * edited beside the menu it sits next to rather than in code.
 */
export const headerButton = { label: "Support our work", href: "/get-involved/support" } as const;

/**
 * Footer.
 *
 * Four columns, and the fourth is the one the client asked for: everything an
 * institutional reader looks for when they are deciding whether an organisation
 * is real. Leadership, registration, reports, policies and safeguarding all sit
 * together rather than being scattered between About and Transparency.
 */
export const footerNav = [
  {
    heading: "Our Work",
    links: [
      { label: "All programmes", href: "/our-work" },
      { label: "Women & Girls", href: "/our-work/women-girls" },
      { label: "Children & Education", href: "/our-work/children-education" },
      { label: "Health, Relief & Community Resilience", href: "/our-work/health-relief-resilience" },
      { label: "Culture, Environment & Dialogue", href: "/our-work/culture-environment-dialogue" },
    ],
  },
  {
    heading: "Evidence",
    links: [
      { label: "Impact", href: "/impact" },
      { label: "Where we work", href: "/where-we-work" },
      { label: "News", href: "/news" },
      { label: "Stories from the field", href: "/stories" },
      { label: "Gallery", href: "/gallery" },
      { label: "Media & dialogue", href: "/media" },
    ],
  },
  {
    heading: "Get Involved",
    links: [
      { label: "Support our work", href: "/get-involved/support" },
      { label: "Volunteer with us", href: "/get-involved/volunteer" },
      { label: "Charity Trek", href: "/get-involved/charity-trek" },
      { label: "All the ways to take part", href: "/get-involved" },
    ],
  },
  {
    heading: "Organisation",
    links: [
      { label: "About us", href: "/about" },
      { label: "Leadership and governance", href: "/about#governance" },
      { label: "Registration", href: "/transparency#registration" },
      { label: "Reports and financials", href: "/transparency#documents" },
      { label: "Policies", href: "/transparency#policies" },
      { label: "Privacy policy", href: "/privacy" },
      { label: "Terms and conditions", href: "/terms" },
      { label: "Cookies and analytics", href: "/cookies" },
      { label: "Safeguarding", href: "/safeguarding" },
      { label: "Contact", href: "/contact" },
    ],
  },
] as const;
